import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Text, mergeClasses, Tooltip, useId } from '@fluentui/react-components';
import { debounce } from 'underscore';
import {
  ColorPicker,
  getColorFromString,
} from '@fluentui/react';
import {
  AddRegular,
  DismissRegular,
  Eyedropper20Regular,
} from '@fluentui/react-icons';
import {
  HiddenDivForAnnounce,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ColorButton } from './color-button';
import { useColorControlStyles } from './style';

const colors = [{
  color: '#000000',
  label: _TL_('Black'),
}, {
  color: '#ffffff',
  label: _TL_('White'),
}, {
  color: '#3a3a3a',
  label: _TL_('Dark gray'),
}, {
  color: '#757575',
  label: _TL_('Gray'),
}, {
  color: '#cbcbcb',
  label: _TL_('Light gray'),
}, {
  color: '#e8e8e8',
  label: _TL_('Very light gray'),
}, {
  color: '#f3f3f3',
  label: _TL_('White smoke color'),
}, {
  color: '#d34e84',
  label: _TL_('Bright pink'),
}, {
  color: '#d44d61',
  label: _TL_('Bright red'),
}, {
  color: '#d56153',
  label: _TL_('Soft red'),
}, {
  color: '#d48956',
  label: _TL_('Soft orange'),
}, {
  color: '#d1d05e',
  label: _TL_('Soft yellow'),
}, {
  color: '#8ed05d',
  label: _TL_('Soft green'),
}, {
  color: '#50d15f',
  label: _TL_('Light green'),
}, {
  color: '#4cd19b',
  label: _TL_('Bright green'),
}, {
  color: '#47bcce',
  label: _TL_('Light cyan'),
}, {
  color: '#4793cd',
  label: _TL_('Light blue'),
}, {
  color: '#4c53cb',
  label: _TL_('Light navy'),
}, {
  color: '#8e52ca',
  label: _TL_('Bright purple'),
}, {
  color: '#d14fc8',
  label: _TL_('Bright pink'),
}];

export const ColorControl = (props) => {
  const {
    i18n,
    color,
    lastColor,
    draw,
    logger,
  } = props;

  const colorControlStyles = useColorControlStyles();
  const colorPicker = useRef(null);
  const addColorButton = useRef(null);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [announcedText, setAnnouncedText] = useState('');
  const wrappedDraw = useCallback((colorValue, ariaLabel) => {
    draw(colorValue);
    setAnnouncedText(i18n.getString(_TL_('Set the color to {{colorValue}}'), { colorValue: ariaLabel || colorValue }));
  }, [draw, i18n]);

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' && event.shiftKey) {
      if (addColorButton.current) {
        addColorButton.current.focus();
      }
      event.preventDefault();
    }
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  useEffect(() => {
    if (showColorPicker && colorPicker.current) {
      const target = colorPicker.current.querySelector('.ms-ColorPicker-colorRect');
      target.focus();
      target.addEventListener('keydown', handleKeyDown);
      return () => {
        target.removeEventListener('keydown', handleKeyDown);
      };
    }
    return () => {};
  }, [showColorPicker]);

  const onAddKeyDown = (event) => {
    if (event.key === 'Enter') {
      toggleColorPicker();
      event.preventDefault();
    }
  };

  const hideEyeDropper = useMemo(() => {
    const isMacEnv = new URLSearchParams(document.location.search).get('isMacEnv');
    return isMacEnv === 'true';
  }, []);

  const logSetColorHelper = useCallback((message, data) => {
    logger.logTrace(`background change: set the color: ${message}`, data);
  }, [logger]);

  const sampleColor = async () => {
    if ('EyeDropper' in window && !hideEyeDropper) {
      // eslint-disable-next-line no-undef
      const ed = new EyeDropper();
      logger.logTrace('background change: open the eye dropper');
      setAnnouncedText(i18n.getString(_TL_('Open eye dropper component to extract color.')));
      const newColor = await ed.open();
      if (newColor) {
        logSetColorHelper('eye dropper color', { color: newColor.sRGBHex });
        wrappedDraw(newColor.sRGBHex);
      }
    }
  };

  const onEyeDropperKeyDown = async (event) => {
    if (event.key === 'Enter') {
      await sampleColor();
      event.preventDefault();
    }
  };

  const debouncedDraw = useMemo(() => debounce(draw, 250), [draw]);
  const selectedColorId = useId('selected-color');
  const suggestedColorId = useId('suggested-color');

  return (
    <div className={mergeClasses(colorControlStyles, 'background-change-color')}>
      <div className="eyeDropper" style={{ backgroundColor: color }}>
        {'EyeDropper' in window && !hideEyeDropper ?
          <Tooltip
            withArrow
            appearance="inverted"
            relationship="label"
            content={i18n.getString(_TL_('Extract color'))}
          >
            <div className={mergeClasses('eye-dropper-button')} role="button" onClick={sampleColor} onKeyDown={onEyeDropperKeyDown} tabIndex={0}>
              <Eyedropper20Regular />
            </div>
          </Tooltip> : null
        }
      </div>
      <div className="design">
        <Text className="design-title" id={selectedColorId}>{i18n.getString(_TL_('Colors used in the image'))}</Text>
        <div className="design-color-chooser" role="group" aria-labelledby={selectedColorId}>
          <Tooltip
            withArrow
            appearance="inverted"
            relationship="label"
            content={showColorPicker ? i18n.getString(_TL_('Close color picker')) : i18n.getString(_TL_('Open color picker'))}
          >
            <div
              className={mergeClasses('add-color-button')}
              role="button"
              onClick={toggleColorPicker}
              onKeyDown={onAddKeyDown}
              ref={addColorButton}
              tabIndex={0}
            >
              {showColorPicker ? <DismissRegular /> : <AddRegular />}
            </div>
          </Tooltip>
          <div className="selected-color-wrapper">
            <ColorButton
              key="selected-color"
              color={color}
              draw={
                (colorValue, ariaLabel) => {
                  wrappedDraw(colorValue, ariaLabel);
                  logSetColorHelper('the selected color', { color: ariaLabel || colorValue });
                }}
              ariaLabel={i18n.getString(_TL_('Selected color'))}
            />
          </div>
          <ColorButton
            key="last-color"
            color={lastColor}
            draw={
              (colorValue, ariaLabel) => {
                wrappedDraw(colorValue, ariaLabel);
                logSetColorHelper('the last selected color', { color: ariaLabel || colorValue });
              }}
            ariaLabel={i18n.getString(_TL_('Last used color'))}
          />
        </div>
        {showColorPicker &&
          <div className="color-picker" ref={colorPicker}>
            <ColorPicker
              color={getColorFromString(color)}
              onChange={(ev, colorObject) => {
                debouncedDraw(colorObject.str);
                logSetColorHelper('the color picker color', { color: colorObject.str });
                setAnnouncedText(i18n.getString(_TL_('Set the color to {{colorValue}}'), { colorValue: colorObject.str }));
              }}
              alphaType="none"
              showPreview
              strings={{
                // By default, the sliders will use the text field labels as their aria labels.
                // Previously this example had more detailed instructions in the labels, but this is
                // a bad practice and not recommended. Labels should be concise, and match visible text when possible.
                hueAriaLabel: i18n.getString(_TL_('Hue')),
                alphaLabel: i18n.getString(_TL_('Alpha')),
                greenLabel: i18n.getString(_TL_('Green')),
                blueLabel: i18n.getString(_TL_('Blue')),
                redLabel: i18n.getString(_TL_('Red')),
              }}
            />
          </div>}
      </div>
      {!showColorPicker &&
        <div className="suggestion">
          <Text className="suggestion-title" id={suggestedColorId}>{i18n.getString(_TL_('Suggestions'))}</Text>
          <div className="suggestion-color-chooser" role="group" aria-labelledby={suggestedColorId}>
            {
              colors.map(item => (
                <ColorButton
                  key={item.color}
                  color={item.color}
                  draw={
                    (colorValue, ariaLabel) => {
                      wrappedDraw(colorValue, ariaLabel);
                      logSetColorHelper('the suggested color', { color: ariaLabel || colorValue });
                    }}
                  ariaLabel={i18n.getString(item.label)}
                />
              ))
            }
          </div>
        </div>}
      <HiddenDivForAnnounce toAnnounce={announcedText} />
    </div>
  );
};
